import styleText from './as-modal-overlay.css?raw';

export class AsModalOverlay extends HTMLElement {
  private dialog: HTMLDialogElement | null = null;
  private panelObserver: MutationObserver | null = null;

  static get observedAttributes() {
    return ['show', 'visible-child', 'mode', 'show-close', 'disable-bg-close'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this._render();
    this.dialog = this.shadowRoot!.querySelector('dialog');

    this.dialog?.addEventListener('close', () => {
      if (this.hasAttribute('show')) this.removeAttribute('show');
      this.dispatchEvent(new CustomEvent('as-modal-closed', { bubbles: true, composed: true }));
    });

    this.shadowRoot!.addEventListener('click', (e) => {
      const path = e.composedPath();
      const isCloseBtn = path.some(el => (el as HTMLElement).classList?.contains('close-button'));
      const isBackdrop = e.target === this.dialog;

      if (isCloseBtn || (isBackdrop && !this.hasAttribute("disable-bg-close"))) {
        this.close();
      }
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.dispatchEvent(new CustomEvent('as-modal-enter-pressed', { bubbles: true, composed: true }));
      }
    });

    this.panelObserver = new MutationObserver(() => this._syncVisibility());
    this.panelObserver.observe(this, { childList: true });
    
    this.addEventListener('as-set-visible-child', (e: any) => this.setAttribute('visible-child', e.detail.id));
    ['open-modal', 'as-open-overlay', 'as-open-modal'].forEach(sig => 
      this.addEventListener(sig, (e: any) => this.open(e.detail?.visibleChild))
    );
    ['close-modal', 'as-close-overlay', 'as-hide-overlay'].forEach(sig => 
      this.addEventListener(sig, () => this.close())
    );

    this._syncVisibility();
    if (this.hasAttribute('show')) this.open();
  }

  disconnectedCallback() {
    this.panelObserver?.disconnect();
  }

  private _render() {
    const showClose = this.getAttribute("show-close") !== "false" && this.hasAttribute("show-close");
    this.shadowRoot!.innerHTML = `
      <style>${styleText}</style>
      <dialog id="modal-container">
        <div class="modal-content">
          ${showClose ? `<button class="close-button" aria-label="Close">×</button>` : ''}
          <slot id="content-slot"></slot>
        </div>
      </dialog>
    `;
  }

  attributeChangedCallback(name: string, old: string, val: string) {
    if (old === val || !this.dialog) return;

    if (name === 'show') {
      if (val !== null) {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        if (this.dialog.open) this.dialog.close();
      }
    }
    
    if (name === 'visible-child' || name === 'mode') {
      this._syncVisibility();
    }
    
    if (name === 'show-close') this._render();
  }

  private _syncVisibility() {
    const mode = this.getAttribute('mode') || 'auto';
    const targetId = this.getAttribute('visible-child');
    const children = Array.from(this.children);
    let shouldDisableBg = false;

    children.forEach(el => {
      if (mode === 'auto') {
        const isTarget = el.id === targetId;
        if (isTarget) {
          (el as HTMLElement).style.display = ''; 
          el.classList.add('visible');
          if (el.hasAttribute('disable-bg-close')) shouldDisableBg = true;
        } else {
          (el as HTMLElement).style.display = 'none';
          el.classList.remove('visible');
        }
      } else {
        (el as HTMLElement).style.display = '';
      }
    });

    shouldDisableBg ? this.setAttribute('disable-bg-close', '') : this.removeAttribute('disable-bg-close');
  }

  open(id?: string) {
    if (id) this.setAttribute('visible-child', id);
    this.setAttribute('show', '');
  }

  close() {
    this.removeAttribute('show');
  }

  show(id?: string) { this.open(id); }
  hide() { this.close(); }
  setVisibleChild(id: string) { this.setAttribute('visible-child', id); }

  /** Id of the child currently shown, or null while the overlay is closed. */
  get visibleChildId(): string | null {
    return this.hasAttribute('show') ? this.getAttribute('visible-child') : null;
  }
}

customElements.define('as-modal-overlay', AsModalOverlay);